import { useState, type ComponentType, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/components';
import PlusIcon from '@/shared/assets/plus-icon.svg?react';
import SpotAccountIcon from '@/shared/assets/account-spot-icon.svg?react';
import MarginAccountIcon from '@/shared/assets/account-margin-icon.svg?react';
import InvestAccountIcon from '@/shared/assets/account-invest-icon.svg?react';
import EarnAccountIcon from '@/shared/assets/account-earn-icon.svg?react';
import { cn } from '@/shared/utils';
import { useMediaQuery } from '@/shared/lib';
import { useOpenAccountAvailabilityStore } from '@/features/account-open';
import { useAccountCreateModal } from '@/widgets/account-create';
import type { AccountType } from '@/entities/account';
import { useSessionStore } from '@/entities/session';
import { useVerificationInfoModal } from '@/entities/verification';
import { ActionMenuItem } from '@/widgets/account-card';

type AddAccountOption = {
  type: AccountType;
  labelKey: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  isAvailable: boolean;
};

const AddAccountButton = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const isTabletUp = useMediaQuery('(min-width: 1068px)');
  const [open, setOpen] = useState(false);
  const { session } = useSessionStore();
  const isVerified = session?.additional?.isVerified || false;
  const { openVerificationInfoModal } = useVerificationInfoModal();

  const {
    openSpotIsAvailable,
    openMarginIsAvailable,
    openInvestIsAvailable,
    openEarnIsAvailable,
    spotCreateIsActive,
    marginCreateIsActive,
    investCreateIsActive,
    earnCreateIsActive,
  } = useOpenAccountAvailabilityStore();

  const { openCreateAccountModal } = useAccountCreateModal();

  const options: Array<AddAccountOption> = [
    {
      type: 'spot',
      labelKey: 'personalAccount.overview.cards.spot',
      icon: SpotAccountIcon,
      isAvailable: openSpotIsAvailable && spotCreateIsActive,
    },
    {
      type: 'leverage',
      labelKey: 'personalAccount.overview.cards.leverage',
      icon: MarginAccountIcon,
      isAvailable: openMarginIsAvailable && marginCreateIsActive,
    },
    {
      type: 'invest',
      labelKey: 'personalAccount.overview.cards.invest',
      icon: InvestAccountIcon,
      isAvailable: openInvestIsAvailable && investCreateIsActive,
    },
    {
      type: 'earn',
      labelKey: 'personalAccount.overview.cards.earn',
      icon: EarnAccountIcon,
      isAvailable: openEarnIsAvailable && earnCreateIsActive,
    },
  ];

  const availableOptions = options.filter(option => option.isAvailable);

  if (!availableOptions.length) {
    return null;
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && !isVerified) {
      openVerificationInfoModal();
      return;
    }

    setOpen(nextOpen);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger
        withChevron={false}
        className={cn(
          'w-auto h-[2.8rem] items-center px-[0.8rem] rounded-[0.8rem]',
          'border border-stroke bg-background text-primary cursor-pointer hover:bg-secondary-bg data-[state=open]:bg-secondary-bg transition-colors',
          className
        )}>
        <span className="flex items-center gap-[0.4rem] headline5 whitespace-nowrap">
          <PlusIcon className="size-[1.6rem] text-primary" />
          {isTabletUp && t('personalAccount.overview.actions.addAccount')}
          <ChevronDownIcon
            className={cn(
              'size-[1.6rem] text-tertiary transition-transform',
              open && 'rotate-180'
            )}
          />
        </span>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-auto min-w-0 max-w-none p-0 rounded-[1.2rem] border border-stroke bg-background/94 shadow-(--shadow-dropdown)"
        classNameViewport="flex flex-col gap-[0.8rem] w-auto min-w-full max-w-none p-[0.4rem]">
        {availableOptions.map(option => (
          <ActionMenuItem
            key={option.type}
            icon={option.icon}
            label={t(option.labelKey)}
            className="h-[3.6rem] rounded-[0.8rem] py-[0.8rem] pr-[1.6rem] pl-[1.2rem]"
            childrenClassName="gap-[0.8rem]"
            iconClassName="size-[1.6rem]"
            labelClassName="medium5 whitespace-nowrap"
            onClick={() => {
              setOpen(false);
              openCreateAccountModal(option.type);
            }}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { AddAccountButton };
